import {
  ChangeDetectorRef,
  Component,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import { Subscription, combineLatest } from 'rxjs';

import { ReportOutputKind, ReportQuery, ReportQueryService } from '../../../services/report-query';
import { DashboardWidgetLayout } from '../dashboard-grid/dashboard-grid';
import {
  DashboardColor,
  DashboardValueFormat,
  dashboardErrorMessage,
  dashboardLayoutStyle,
  formatDashboardValue,
  loadDashboardScalar,
} from '../../widget-and-graphs.models';

export interface DashboardProgressMetric {
  label: string;
  query: ReportQuery;
  output?: Extract<ReportOutputKind, 'number' | 'percentage'>;
  format?: DashboardValueFormat;
  color?: DashboardColor;
  maximum?: number;
}

interface LoadedProgressMetric extends DashboardProgressMetric {
  value: number;
  formattedValue: string;
  progress: number;
}

@Component({
  selector: 'app-dashboard-welcome-widget',
  standalone: false,
  templateUrl: './dashboard-welcome-widget.html',
  styleUrl: './dashboard-welcome-widget.scss',
})
export class DashboardWelcomeWidget implements OnChanges, OnDestroy {
  @Input() iconSvg = '';
  @Input({ required: true }) title = '';
  @Input() subtext = '';
  @Input({ required: true }) metrics: DashboardProgressMetric[] = [];
  @Input() illustrationUrl = '';
  @Input() illustrationAlt = '';
  @Input() currencyCode = 'USD';
  @Input() locale = 'en-US';
  @Input() layout: DashboardWidgetLayout = { xs: { width: 12, height: 5 } };

  loading = false;
  error = '';
  loadedMetrics: LoadedProgressMetric[] = [];
  private request?: Subscription;

  constructor(
    private readonly reportQuery: ReportQueryService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  @HostBinding('attr.style')
  get layoutStyles(): string {
    return dashboardLayoutStyle(this.layout);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['metrics']) this.load();
  }

  ngOnDestroy(): void {
    this.request?.unsubscribe();
  }

  private load(): void {
    this.request?.unsubscribe();
    this.loadedMetrics = [];
    this.error = '';
    if (this.metrics.length !== 2) {
      this.error = 'The welcome widget requires exactly two metric queries.';
      return;
    }
    this.loading = true;
    this.request = combineLatest(
      this.metrics.map((metric) =>
        loadDashboardScalar(this.reportQuery, metric.query, metric.output ?? 'number'),
      ),
    ).subscribe({
      next: (results) => {
        this.loadedMetrics = results.map((result, index) => {
          const metric = this.metrics[index];
          const maximum = Math.max(0.000001, metric.maximum ?? 100);
          return {
            ...metric,
            value: result.value,
            formattedValue: formatDashboardValue(
              result.value,
              metric.format ?? (result.dataType === 'percentage' ? 'percentage' : 'number'),
              this.currencyCode,
              this.locale,
            ),
            progress: Math.max(0, Math.min(100, (result.value / maximum) * 100)),
          };
        });
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.error = dashboardErrorMessage(error);
        this.loading = false;
        this.cdr.detectChanges();
      },
    });
  }
}
